import { Button } from '@creditornot/cb-button';
import { InfoCircleFilled, Pencil, Plus } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { OpeningTimes as OpeningTimesT } from 'apis/restaurant-api/merchant-admin/venue/types';
import { fromWeeklyScheduleToTimePairsByWeekday } from 'common/components/weekly-schedules';
import { Typography as T } from 'common/typography';
import time from 'common/utils/time';
import { WEEKDAYS } from 'common/utils/time/values';

import EffectiveOpeningTimes from './EffectiveOpeningTimes';
import { isOpeningTimesEmpty } from '../utils';

type Props = {
  effectiveOpeningTimes: OpeningTimesT | null;
  onEdit?: () => void;
  times: OpeningTimesT | null;
  timezone: string;
};

const weekdays: { [day: string]: string } = {
  monday: 'week.mon',
  tuesday: 'week.tue',
  wednesday: 'week.wed',
  thursday: 'week.thu',
  friday: 'week.fri',
  saturday: 'week.sat',
  sunday: 'week.sun',
};

const ButtonContainer = styled.div`
  margin-inline-start: auto;
  margin-block: -6px; /** to not affect the header spacings */
`;

const EditButton = styled(Button).attrs({ size: 'small', variant: 'secondary', icon: <Pencil /> })``;

const Root = styled.div``;

const Header = styled(T.Heading6)`
  color: ${cbSemanticCssVars.textPrimary};
  display: flex;
  align-items: center;
  margin-block-end: 24px;
  flex-wrap: wrap;
  gap: 12px 8px;
`;

const EffectiveOpeningTimesInfoIcon = styled(InfoCircleFilled).attrs({ height: 12 })`
  fill: ${cbSemanticCssVars.textSecondary};
`;

const EffectiveOpeningTimesContainer = styled.div`
  width: 313px;
  padding: 32px;
`;

const DayField = styled(T.Body3)`
  width: 100%;
  margin-block-end: 12px;
  display: flex;
  justify-content: space-between;
  color: ${cbSemanticCssVars.textPrimary};
  text-align: end;
  text-transform: capitalize;
`;

const Times = styled(T.Body3)`
  display: flex;
  flex-flow: column;
  margin-inline-start: 4px;
`;

const AddButton = styled(Button).attrs({ size: 'small', variant: 'secondary' })`
  margin-block-start: 24px;
  display: block;
`;

const OpeningTimesView = ({ effectiveOpeningTimes, onEdit, times, timezone }: Props) => {
  const transformedTimes = useMemo(
    () => (times === null || isOpeningTimesEmpty(times) ? null : fromWeeklyScheduleToTimePairsByWeekday(times)),
    [times],
  );

  return (
    <Root>
      <Header>
        <FormattedMessage id="venue-details.opening-times" />
        {effectiveOpeningTimes && (
          <Popover
            placement="bottom"
            openOnHover
            offset={8}
            content={
              <EffectiveOpeningTimesContainer>
                <EffectiveOpeningTimes effectiveOpeningTimes={effectiveOpeningTimes} timezone={timezone} />
              </EffectiveOpeningTimesContainer>
            }
          >
            {({ refProps }) => <EffectiveOpeningTimesInfoIcon {...refProps} />}
          </Popover>
        )}
        {transformedTimes && onEdit && (
          <ButtonContainer>
            <EditButton onClick={onEdit}>
              <FormattedMessage id="state.edit" />
            </EditButton>
          </ButtonContainer>
        )}
      </Header>
      {transformedTimes ? (
        WEEKDAYS.map(
          day =>
            transformedTimes[day] && (
              <DayField key={day}>
                <T.Body3>
                  <FormattedMessage id={weekdays[day]} />
                </T.Body3>
                <Times>
                  {transformedTimes[day].length ? (
                    transformedTimes[day].map((hours, i) => (
                      <T.Body3 key={i}>
                        {time.renderMinutesFromMidnight(hours.start, false)}-
                        {time.renderMinutesFromMidnight(hours.end, true)}
                      </T.Body3>
                    ))
                  ) : (
                    <FormattedMessage id="opening-hours.closed" />
                  )}
                </Times>
              </DayField>
            ),
        )
      ) : (
        <div>
          <T.Body3>
            <FormattedMessage id="venue-details.no-opening-times-info" />
          </T.Body3>
          {onEdit && (
            <AddButton icon={<Plus />} onClick={onEdit}>
              <FormattedMessage id="venue-details.add-entry" />
            </AddButton>
          )}
        </div>
      )}
    </Root>
  );
};

export default OpeningTimesView;
